import { reaction } from 'mobx'
import { GenericStore } from '../../../util/mobx/generic-store/generic.store'
import { createStore } from '../../../util/mobx/generic-store/store-creator'
import { initializeAnalytics } from '../util/analytics-loader'

export interface CookieConsent {
  necessary: boolean
  analytics: boolean
  marketing: boolean
  preferences: boolean
}

export class CookieConsentStore extends GenericStore {
  hasConsented: boolean | null = null
  lastUpdated: number | null = null
  consent: CookieConsent = {
    necessary: true,
    analytics: false,
    marketing: false,
    preferences: false,
  }

  constructor() {
    super('CookieConsentStore')
    super.observe(this)
    super.persist({ encrypt: false })

    // Auto-initialize analytics when consent is granted
    reaction(
      () => this.hasAnalyticsConsent,
      (hasConsent) => {
        if (hasConsent) {
          initializeAnalytics()
        }
      },
      { fireImmediately: true },
    )
  }

  get shouldShowBanner(): boolean {
    return this.hasConsented === null
  }

  get hasAnalyticsConsent(): boolean {
    return this.consent.analytics
  }

  get hasMarketingConsent(): boolean {
    return this.consent.marketing
  }

  get hasPreferencesConsent(): boolean {
    return this.consent.preferences
  }

  acceptAll() {
    this.consent = {
      necessary: true,
      analytics: true,
      marketing: true,
      preferences: true,
    }
    this.hasConsented = true
    this.lastUpdated = Date.now()
  }

  acceptNecessaryOnly() {
    this.consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    }
    this.hasConsented = true
    this.lastUpdated = Date.now()
  }

  setCustomConsent(consent: Partial<CookieConsent>) {
    this.consent = {
      ...this.consent,
      ...consent,
      necessary: true,
    }
    this.hasConsented = true
    this.lastUpdated = Date.now()
  }

  resetConsent() {
    this.hasConsented = null
    this.lastUpdated = null
    this.consent = {
      necessary: true,
      analytics: false,
      marketing: false,
      preferences: false,
    }
  }
}

export const cookieConsentStoreInstance = new CookieConsentStore()

export const {
  useStore: useCookieConsentStore,
  StoreProvider: CookieConsentStoreProvider,
} = createStore(cookieConsentStoreInstance)
